import { 
  Prompt, 
  PromptListResponse, 
  Category, 
  Tag, 
  SearchFilters, 
  Statistics,
  UsageLog 
} from '../types';

// 로컬 데이터 사용
import { getPrompts, getPromptById, getCategories, getTags } from '../data/prompts.js';

// Prompt API
export const promptAPI = {
  // 프롬프트 목록 조회
  getPrompts: async (filters: SearchFilters = {}): Promise<PromptListResponse> => {
    return await getPrompts(filters);
  },

  // 특정 프롬프트 조회
  getPromptById: async (id: number): Promise<Prompt> => {
    const prompt = await getPromptById(id);
    if (!prompt) {
      throw new Error('프롬프트를 찾을 수 없습니다');
    }
    return prompt;
  },

  // 프롬프트 생성 (로컬에서는 지원하지 않음)
  createPrompt: async (prompt: Omit<Prompt, 'id'>): Promise<Prompt> => {
    throw new Error('로컬 모드에서는 프롬프트 생성이 지원되지 않습니다');
  },

  // 프롬프트 수정 (로컬에서는 지원하지 않음)
  updatePrompt: async (id: number, updates: Partial<Prompt>): Promise<Prompt> => {
    throw new Error('로컬 모드에서는 프롬프트 수정이 지원되지 않습니다');
  },

  // 프롬프트 삭제 (로컬에서는 지원하지 않음)
  deletePrompt: async (id: number): Promise<void> => {
    throw new Error('로컬 모드에서는 프롬프트 삭제가 지원되지 않습니다');
  },

  // 검색
  searchPrompts: async (query: string, filters: SearchFilters = {}): Promise<PromptListResponse> => {
    return await getPrompts({ ...filters, search: query });
  },

  // 사용 로그 (로컬에서는 기본 응답)
  logUsage: async (promptId: number, data: any): Promise<UsageLog> => {
    return {
      id: Date.now(),
      prompt_id: promptId,
      used_at: new Date().toISOString(),
      user_feedback: data.feedback,
      feedback_comment: data.feedback_comment,
    };
  },
};

// Category API
export const categoryAPI = {
  // 카테고리 목록 조회
  getCategories: async (): Promise<Category[]> => {
    return await getCategories();
  },

  // 특정 카테고리 조회
  getCategoryById: async (id: number): Promise<Category> => {
    const categories = await getCategories();
    const category = categories.find(c => c.id === id);
    if (!category) {
      throw new Error('카테고리를 찾을 수 없습니다');
    }
    return category;
  },
};

// Tag API
export const tagAPI = {
  // 태그 목록 조회
  getTags: async (): Promise<Tag[]> => {
    return await getTags();
  },

  // 인기 태그 조회
  getPopularTags: async (limit: number = 10): Promise<Tag[]> => {
    const tags = await getTags();
    return tags.slice(0, limit);
  },
};

// User API (로컬 모드에서는 기본 응답)
export const userAPI = {
  // 사용자 통계
  getUserStats: async (): Promise<Statistics> => {
    const prompts = await getPrompts({});
    const categories = await getCategories();
    const tags = await getTags();
    
    return {
      total_prompts: prompts.pagination.total,
      total_categories: categories.length,
      total_tags: tags.length,
      total_usage: 0,
    };
  },

  // 인기 프롬프트
  getPopularPrompts: async (limit: number = 10): Promise<Prompt[]> => {
    const result = await getPrompts({ limit });
    return result.prompts;
  },

  // 사용 로그 (로컬에서는 지원하지 않음)
  logUsage: async (promptId: number, feedback?: number): Promise<UsageLog> => {
    throw new Error('로컬 모드에서는 사용 로그가 지원되지 않습니다');
  },
};